// src/components/dashboard/Statistics.tsx
// ✅ CORRIGIDO: AbortController em todos os loads + dependências corretas no useEffect

import React, { useState, useEffect } from 'react';
import {
    Target,
    Flame,
    BookOpen,
    Clock,
    TrendingUp,
    Award,
    Activity,
    BarChart3,
} from 'lucide-react';
import { DashboardStats, DifficultCard, WeekHistory } from './types';
import './Statistics.css';

import TodaySessionsChart from './stats/TodaySessionsChart';
import CircularProgressIndicator from './stats/CircularProgressIndicator';
import MonthLineChart from './stats/MonthLineChart';
import TopDecksMonth from './stats/TopDecksMonth';
import { useStatsAnimation } from '../../hooks/useStatsAnimation';
import { TodaySession, MonthHistoryDay, TopDeckMonth } from './types';
import DifficultCardsToday from './stats/DifficultCardsToday';
import HistoryYearChart from './stats/HistoryYearChart';
import { WeekChart } from './stats/WeekChart';
import { StatsSubTab } from './Sidebar';
import QuestionStatistics from './QuestionStatistics';

interface StatisticsProps {
    stats: DashboardStats | null;
    weekHistory: WeekHistory[];
    onReload: () => void;
    activeTab?: StatsSubTab;
    hideTimeCard?: boolean;
}

type ViewType = 'today' | 'week' | 'month' | 'history';

const Statistics: React.FC<StatisticsProps> = ({
    stats,
    weekHistory,
    onReload,
    activeTab = 'flashcards',
    hideTimeCard = false
}) => {
    const [activeView, setActiveView] = useState<ViewType>('today');

    const [todaySessions, setTodaySessions] = useState<TodaySession[]>([]);
    const [monthHistory, setMonthHistory] = useState<MonthHistoryDay[]>([]);
    const [topDecksMonth, setTopDecksMonth] = useState<TopDeckMonth[]>([]);
    const { shouldAnimate, markAnimated } = useStatsAnimation();
    const [difficultCards, setDifficultCards] = useState<DifficultCard[]>([]);
    const [yearHistory, setYearHistory] = useState<any[]>([]);

    // ✅ FIX: Cada função de load aceita AbortSignal para cancelamento correto
    const loadTodaySessions = async (signal?: AbortSignal) => {
        try {
            const token = localStorage.getItem('accessToken');
            const response = await fetch('/api/stats/today-sessions', {
                headers: { Authorization: `Bearer ${token}` },
                signal
            });
            if (response.ok) {
                const data = await response.json();
                setTodaySessions(data.sessions || []);
            }
        } catch (error: any) {
            if (error?.name !== 'AbortError') {
                console.error('Erro ao carregar sessões:', error);
            }
        }
    };

    const loadMonthHistory = async (signal?: AbortSignal) => {
        try {
            const token = localStorage.getItem('accessToken');
            const response = await fetch('/api/stats/month-history', {
                headers: { Authorization: `Bearer ${token}` },
                signal
            });
            if (response.ok) {
                const data = await response.json();
                setMonthHistory(data.history || []);
            }
        } catch (error: any) {
            if (error?.name !== 'AbortError') {
                console.error('Erro ao carregar histórico mensal:', error);
            }
        }
    };

    const loadTopDecksMonth = async (signal?: AbortSignal) => {
        try {
            const token = localStorage.getItem('accessToken');
            const response = await fetch('/api/stats/top-decks-month', {
                headers: { Authorization: `Bearer ${token}` },
                signal
            });
            if (response.ok) {
                const data = await response.json();
                setTopDecksMonth(data.topDecks || []);
            }
        } catch (error: any) {
            if (error?.name !== 'AbortError') {
                console.error('Erro ao carregar top decks:', error);
            }
        }
    };

    const loadDifficultCards = async (signal?: AbortSignal) => {
        try {
            const token = localStorage.getItem('accessToken');
            const response = await fetch('/api/stats/difficult-cards-today', {
                headers: { Authorization: `Bearer ${token}` },
                signal
            });
            if (response.ok) {
                const data = await response.json();
                setDifficultCards(data.difficultCards || []);
            }
        } catch (error: any) {
            if (error?.name !== 'AbortError') {
                console.error('Erro ao carregar cards difíceis:', error);
            }
        }
    };

    const loadYearHistory = async (signal?: AbortSignal) => {
        try {
            const token = localStorage.getItem('accessToken');
            const response = await fetch('/api/stats/year-history', {
                headers: { Authorization: `Bearer ${token}` },
                signal
            });
            if (response.ok) {
                const data = await response.json();
                setYearHistory(data.history || []);
            }
        } catch (error: any) {
            if (error?.name !== 'AbortError') {
                console.error('Erro ao carregar histórico anual:', error);
            }
        }
    };

    // ✅ FIX CRÍTICO: Dependência APENAS em [activeView].
    //
    // O bug anterior incluía [markAnimated, shouldAnimate] nas dependências.
    // Essas funções vêm de useStatsAnimation() e são recriadas a cada render
    // sem useCallback — então tinham nova referência a cada ciclo.
    // Isso causava o loop:
    //   render → effect dispara → chama markAnimated → hook re-renderiza
    //   → novas referências → effect dispara de novo → loop infinito
    //   → centenas de requisições simultâneas → ERR_INSUFFICIENT_RESOURCES
    //
    // Solução: remover essas funções das dependências (são estáveis na prática)
    // e usar AbortController para cancelar requests ao trocar de aba.
    useEffect(() => {
        const abortController = new AbortController();
        const signal = abortController.signal;
        let weekTimer: ReturnType<typeof setTimeout> | null = null;

        if (activeView === 'today') {
            loadTodaySessions(signal);
            loadDifficultCards(signal);
            if (shouldAnimate('today')) markAnimated('today');
        } else if (activeView === 'week') {
            if (shouldAnimate('week')) {
                weekTimer = setTimeout(() => {
                    if (!signal.aborted) markAnimated('week');
                }, 100);
            }
        } else if (activeView === 'month') {
            loadMonthHistory(signal);
            loadTopDecksMonth(signal);
            if (shouldAnimate('month')) markAnimated('month');
        } else if (activeView === 'history') {
            loadYearHistory(signal);
        }

        // Cleanup: cancela requests em andamento ao trocar de aba ou desmontar
        return () => {
            abortController.abort();
            if (weekTimer) clearTimeout(weekTimer);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeView]);

    // ========== QUESTÕES ==========
    if (activeTab === 'questions') {
        return <QuestionStatistics />;
    }

    // ========== LOADING ==========
    if (!stats) {
        return (
            <div className="dsb-estx-container">
                <div className="dsb-estx-loading">
                    <div className="dsb-estx-spinner"></div>
                    <p>Carregando estatísticas</p>
                </div>
            </div>
        );
    }

    // ========== VIEWS DE FLASHCARDS ==========
    const renderTodayStats = () => {
        const { today, comparison } = stats;
        return (
            <>
                <div className="dsb-estx-hj-grid">
                    <div className="dsb-estx-hj-card highlight">
                        <Target className="dsb-estx-hj-icon" />
                        <div className="dsb-estx-hj-content">
                            <h3 className="dsb-estx-hj-value">{today.cardsStudied}</h3>
                            <p className="dsb-estx-hj-label">Flashcards estudados</p>
                            {comparison.cardsVsYesterday !== 0 && (
                                <span className={`dsb-estx-hj-comparison ${comparison.cardsVsYesterday > 0 ? 'positive' : 'negative'}`}>
                                    {comparison.cardsVsYesterday > 0 ? '+' : ''}{comparison.cardsVsYesterday} vs ontem
                                </span>
                            )}
                        </div>
                    </div>
                    <div className="dsb-estx-hj-card">
                        <Award className="dsb-estx-hj-icon" />
                        <div className="dsb-estx-hj-content">
                            <h3 className="dsb-estx-hj-value">{today.cardsMastered}</h3>
                            <p className="dsb-estx-hj-label">Cards dominados hoje</p>
                            <p className="dsb-estx-hj-detail">Domínio alcançado</p>
                        </div>
                    </div>
                    <div className="dsb-estx-hj-card">
                        <Activity className="dsb-estx-hj-icon" />
                        <div className="dsb-estx-hj-content">
                            <h3 className="dsb-estx-hj-value">{today.accuracy.toFixed(0)}%</h3>
                            <p className="dsb-estx-hj-label">Taxa de acerto</p>
                            <p className="dsb-estx-hj-detail">Precisão hoje</p>
                        </div>
                    </div>
                    {!hideTimeCard && (
                    <div className="dsb-estx-hj-card">
                        <Clock className="dsb-estx-hj-icon" />
                        <div className="dsb-estx-hj-content">
                            <h3 className="dsb-estx-hj-value">{today.timeSpentFormatted}</h3>
                            <p className="dsb-estx-hj-label">Tempo de estudo</p>
                            <p className="dsb-estx-hj-detail">{today.sessions} sessões realizadas</p>
                        </div>
                    </div>
                    )}
                </div>
                <TodaySessionsChart sessions={todaySessions} animate={shouldAnimate('today')} />
                <div className="dsb-estx-hj-bottom-grid">
                    <CircularProgressIndicator
                        cardsStudied={today.cardsStudied}
                        cardsMastered={today.cardsMastered}
                        totalCards={today.cardsStudied}
                        animate={shouldAnimate('today')}
                    />
                    <DifficultCardsToday cards={difficultCards} />
                </div>
            </>
        );
    };

    const renderWeekStats = () => {
        const { week } = stats;
        return (
            <>
                <div className="dsb-estx-sem-grid">
                    <div className="dsb-estx-sem-card highlight">
                        <BookOpen className="dsb-estx-sem-icon" />
                        <div className="dsb-estx-sem-content">
                            <h3 className="dsb-estx-sem-value">{week.totalCards}</h3>
                            <p className="dsb-estx-sem-label">Cards esta semana</p>
                            <p className="dsb-estx-sem-detail">Total estudado</p>
                        </div>
                    </div>
                    <div className="dsb-estx-sem-card">
                        <TrendingUp className="dsb-estx-sem-icon" />
                        <div className="dsb-estx-sem-content">
                            <h3 className="dsb-estx-sem-value">{week.productiveDays}</h3>
                            <p className="dsb-estx-sem-label">Dias produtivos</p>
                            <p className="dsb-estx-sem-detail">Acima da média pessoal</p>
                        </div>
                    </div>
                    <div className="dsb-estx-sem-card">
                        <BarChart3 className="dsb-estx-sem-icon" />
                        <div className="dsb-estx-sem-content">
                            <h3 className="dsb-estx-sem-value">{week.avgCardsPerDay.toFixed(0)}</h3>
                            <p className="dsb-estx-sem-label">Média diária</p>
                            <p className="dsb-estx-sem-detail">Flashcards por dia</p>
                        </div>
                    </div>
                    {!hideTimeCard && (
                    <div className="dsb-estx-sem-card">
                        <Clock className="dsb-estx-sem-icon" />
                        <div className="dsb-estx-sem-content">
                            <h3 className="dsb-estx-sem-value">{week.totalTimeFormatted}</h3>
                            <p className="dsb-estx-sem-label">Tempo total</p>
                            <p className="dsb-estx-sem-detail">{week.daysStudied} dias de estudo</p>
                        </div>
                    </div>
                    )}
                </div>
                <WeekChart weekHistory={weekHistory} />
            </>
        );
    };

    const renderMonthStats = () => {
        const { month } = stats;
        return (
            <>
                <div className="dsb-estx-mes-grid">
                    <div className="dsb-estx-mes-card highlight">
                        <BookOpen className="dsb-estx-mes-icon" />
                        <div className="dsb-estx-mes-content">
                            <h3 className="dsb-estx-mes-value">{month.totalCards}</h3>
                            <p className="dsb-estx-mes-label">Total no mês</p>
                            <p className="dsb-estx-mes-detail">Flashcards estudados</p>
                        </div>
                    </div>
                    <div className="dsb-estx-mes-card">
                        <TrendingUp className="dsb-estx-mes-icon" />
                        <div className="dsb-estx-mes-content">
                            <h3 className="dsb-estx-mes-value">{month.daysStudied}</h3>
                            <p className="dsb-estx-mes-label">Dias de estudo</p>
                            <p className="dsb-estx-mes-detail">Frequência mensal</p>
                        </div>
                    </div>
                    <div className="dsb-estx-mes-card">
                        <BarChart3 className="dsb-estx-mes-icon" />
                        <div className="dsb-estx-mes-content">
                            <h3 className="dsb-estx-mes-value">{month.avgCardsPerDay.toFixed(0)}</h3>
                            <p className="dsb-estx-mes-label">Média diária</p>
                            <p className="dsb-estx-mes-detail">Cards por dia no mês</p>
                        </div>
                    </div>
                    {!hideTimeCard && (
                    <div className="dsb-estx-mes-card">
                        <Clock className="dsb-estx-mes-icon" />
                        <div className="dsb-estx-mes-content">
                            <h3 className="dsb-estx-mes-value">{month.totalTimeFormatted}</h3>
                            <p className="dsb-estx-mes-label">Tempo total</p>
                            <p className="dsb-estx-mes-detail">{month.productiveDays} dias produtivos</p>
                        </div>
                    </div>
                    )}
                </div>
                <MonthLineChart history={monthHistory} animate={true} />
                <TopDecksMonth topDecks={topDecksMonth} />
            </>
        );
    };

    const renderHistoryStats = () => {
        const { allTime } = stats;
        return (
            <>
                <div className="dsb-estx-hist-grid">
                    <div className="dsb-estx-hist-card highlight">
                        <Target className="dsb-estx-hist-icon" />
                        <div className="dsb-estx-hist-content">
                            <h3 className="dsb-estx-hist-value">{allTime.totalCardsStudied}</h3>
                            <p className="dsb-estx-hist-label">Total histórico</p>
                            <p className="dsb-estx-hist-detail">Todos os flashcards</p>
                        </div>
                    </div>
                    <div className="dsb-estx-hist-card">
                        <Flame className="dsb-estx-hist-icon" />
                        <div className="dsb-estx-hist-content">
                            <h3 className="dsb-estx-hist-value">{allTime.longestStreak}</h3>
                            <p className="dsb-estx-hist-label">Maior sequência</p>
                            <p className="dsb-estx-hist-detail">Recorde pessoal</p>
                        </div>
                    </div>
                    <div className="dsb-estx-hist-card">
                        <Award className="dsb-estx-hist-icon" />
                        <div className="dsb-estx-hist-content">
                            <h3 className="dsb-estx-hist-value">{allTime.cardsMastered}</h3>
                            <p className="dsb-estx-hist-label">Cards dominados</p>
                        </div>
                    </div>
                    {!hideTimeCard && (
                    <div className="dsb-estx-hist-card">
                        <Clock className="dsb-estx-hist-icon" />
                        <div className="dsb-estx-hist-content">
                            <h3 className="dsb-estx-hist-value">{allTime.totalTimeFormatted}</h3>
                            <p className="dsb-estx-hist-label">Tempo total</p>
                            <p className="dsb-estx-hist-detail">{allTime.totalSessions} sessões</p>
                        </div>
                    </div>
                    )}
                </div>
                <HistoryYearChart history={yearHistory} animate={shouldAnimate('history')} />
            </>
        );
    };

    // ========== RENDER PRINCIPAL (FLASHCARDS) ==========
    return (
        <div className="dsb-estx-container">
            <div className="dsb-estx-view-selector">
                <button
                    className={`dsb-estx-view-btn ${activeView === 'today' ? 'active' : ''}`}
                    onClick={() => setActiveView('today')}
                >
                    Hoje
                </button>
                <button
                    className={`dsb-estx-view-btn ${activeView === 'week' ? 'active' : ''}`}
                    onClick={() => setActiveView('week')}
                >
                    Semana
                </button>
                <button
                    className={`dsb-estx-view-btn ${activeView === 'month' ? 'active' : ''}`}
                    onClick={() => setActiveView('month')}
                >
                    Mês
                </button>
                <button
                    className={`dsb-estx-view-btn ${activeView === 'history' ? 'active' : ''}`}
                    onClick={() => setActiveView('history')}
                >
                    Histórico
                </button>
            </div>

            {activeView === 'today' && renderTodayStats()}
            {activeView === 'week' && renderWeekStats()}
            {activeView === 'month' && renderMonthStats()}
            {activeView === 'history' && renderHistoryStats()}
        </div>
    );
};

export default Statistics;